import { ApiPermission } from 'lib/api-permissions';

export enum ApiPermissionType {
  ACCOUNT = 'Account',
  API = 'API',
  CASE = 'Case',
  GRAPH = 'Graph',
  INQUIRY = 'Inquiry',
  PLATFORM = 'Platform',
  REPORT = 'Report',
  TEAM = 'Team',
  TRANSACTION = 'Transaction',
  VERIFICATION = 'Verification',
  WEBHOOK = 'Webhook',
  WORKFLOW = 'Workflow',
}

export type ApiPermissionDisplayConfig = {
  name: string;
  description: string;
  type: ApiPermissionType;
};

// Do not annotate this with Partial. Instead rely on TypeScript's structural typing.
// This ensures we never access this object with an unknown permission.
export const API_PERMISSION_DISPLAY_CONFIG: Record<ApiPermission, ApiPermissionDisplayConfig> = {
  [ApiPermission.AccountRead]: {
    name: 'Read Account',
    description: 'Access all accounts',
    type: ApiPermissionType.ACCOUNT,
  },
  [ApiPermission.AccountWrite]: {
    name: 'Write Account',
    description: 'Create or update accounts',
    type: ApiPermissionType.ACCOUNT,
  },
  [ApiPermission.ApiKeyRead]: {
    name: 'Read API Key',
    description: 'Access all API keys',
    type: ApiPermissionType.API,
  },
  [ApiPermission.ApiKeyScim]: {
    name: 'Use API Key for SCIM',
    description: 'Use API Key for SCIM',
    type: ApiPermissionType.API,
  },
  [ApiPermission.ApiKeyWrite]: {
    name: 'Write API Key',
    description: 'Create or update API keys',
    type: ApiPermissionType.API,
  },
  [ApiPermission.ApiLogRead]: {
    name: 'Read API Log',
    description: 'Access all API logs',
    type: ApiPermissionType.API,
  },
  [ApiPermission.CaseRead]: {
    name: 'Read Case',
    description: 'Access all cases',
    type: ApiPermissionType.CASE,
  },
  [ApiPermission.CaseWrite]: {
    name: 'Write Case',
    description: 'Create or update cases',
    type: ApiPermissionType.CASE,
  },
  [ApiPermission.DocumentRead]: {
    name: 'Read Document',
    description: 'Access all documents',
    type: ApiPermissionType.PLATFORM,
  },
  [ApiPermission.DocumentWrite]: {
    name: 'Write Document',
    description: 'Create or update documents',
    type: ApiPermissionType.PLATFORM,
  },
  [ApiPermission.EventRead]: {
    name: 'Read Event',
    description: 'Access all events',
    type: ApiPermissionType.PLATFORM,
  },
  [ApiPermission.GraphRead]: {
    name: 'Read Graph',
    description: 'Access graph query results',
    type: ApiPermissionType.GRAPH,
  },
  [ApiPermission.GraphWrite]: {
    name: 'Write Graph',
    description: 'Perform graph queries',
    type: ApiPermissionType.GRAPH,
  },
  [ApiPermission.ImporterRead]: {
    name: 'Read Importer',
    description: 'Access all importers',
    type: ApiPermissionType.PLATFORM,
  },
  [ApiPermission.ImporterWrite]: {
    name: 'Write Importer',
    description: 'Create importers',
    type: ApiPermissionType.PLATFORM,
  },
  [ApiPermission.InquiryRead]: {
    name: 'Read Inquiry',
    description: 'Access all inquiries',
    type: ApiPermissionType.INQUIRY,
  },
  [ApiPermission.InquiryWrite]: {
    name: 'Write Inquiry',
    description: 'Create inquiries',
    type: ApiPermissionType.INQUIRY,
  },
  [ApiPermission.ListRead]: {
    name: 'Read List',
    description: 'Access all lists',
    type: ApiPermissionType.PLATFORM,
  },
  [ApiPermission.ListWrite]: {
    name: 'Write List',
    description: 'Create or update lists',
    type: ApiPermissionType.PLATFORM,
  },
  [ApiPermission.ReportRead]: {
    name: 'Read Report',
    description: 'Access all reports',
    type: ApiPermissionType.REPORT,
  },
  [ApiPermission.ReportWrite]: {
    name: 'Write Report',
    description: 'Create reports',
    type: ApiPermissionType.REPORT,
  },
  [ApiPermission.TxnRead]: {
    name: 'Read Transaction',
    description: 'Access all transactions',
    type: ApiPermissionType.TRANSACTION,
  },
  [ApiPermission.TxnWrite]: {
    name: 'Write Transaction',
    description: 'Create or update transactions',
    type: ApiPermissionType.TRANSACTION,
  },
  [ApiPermission.UserAuditLogRead]: {
    name: 'Read User Audit Log',
    description: 'Access all user audit logs',
    type: ApiPermissionType.TEAM,
  },
  [ApiPermission.VerificationRead]: {
    name: 'Read Verification',
    description: 'Access all verifications',
    type: ApiPermissionType.VERIFICATION,
  },
  [ApiPermission.VerificationWrite]: {
    name: 'Write Verification',
    description: 'Create verifications',
    type: ApiPermissionType.VERIFICATION,
  },
  [ApiPermission.WebhookRead]: {
    name: 'Read Webhook',
    description: 'Access all webhooks',
    type: ApiPermissionType.WEBHOOK,
  },
  [ApiPermission.WebhookWrite]: {
    name: 'Write Webhook',
    description: 'Create or update webhooks',
    type: ApiPermissionType.WEBHOOK,
  },
  [ApiPermission.WorkflowTrigger]: {
    name: 'Trigger Workflow',
    description: 'Trigger a workflow',
    type: ApiPermissionType.WORKFLOW,
  },
};
